/**
 * **arcade テーマの HUD**（Issue #523）。1 人 1 枚の枠とターンの紋章。
 *
 * ## 盤と違って DOM で描く（ユーザー判断 2026-09-24）
 *
 * 盤の中身は**平面の紙**に canvas で描いて 4 点へ貼る（遠近を付けるため）。HUD は平面に乗らないので
 * その動機が無く、逆に要るのが**ガラス風のぼかし**（`backdrop-filter`）・**文字の省略**（`…`）で、
 * canvas だと自前で書くことになる。だから DOM（ファイル名だけ `drawField` と揃えてある）。
 *
 * ## 場所は「名前」で答える
 *
 * 公開するのは `core/hud` の `ANCHOR_PORT`（`plate` / `life` / `hand` / `turnBadge`）だけ。
 * 箱の持ち方はこのファイルの自由で、名前さえ守れば作り替えても、あとで繋ぐ演出は無傷になる。
 * `life` / `hand` の fallback と文字の下限は `core/hud` が持つ。
 *
 * **枠の内側の辺は盤の辺に沿う**（`options.plateSlope: 'board'`）—— `anchors` が `view`
 * （盤の見え方）を受け取るので、カメラを振っても崩れない。盤が写っていなければ既定の数値に落ちる。
 *
 * 見た目は旧演出プレビュー（Issue #639 で削除）の格ゲー風を踏襲した（寸法の考え方だけ写して `.de-hud-arcade` に閉じてある）。
 * 書体は端末のもの（GUI の「書体」＝ `--hud-font` で変えられる。源真ゴシックは climax テーマが使う）。
 */
import { withDefaults } from '../../core/theme';
import { INK_CLASS, strokeCss } from '../../core/decor';
import { fontPx, mirrorBox, partCss, partStyle } from '../../core/hud';
import './arcade.css';

/* ------------------------------------------------------------------ *
 *  触れるもの（宣言）と既定値
 * ------------------------------------------------------------------ */

/**
 * **このテーマだけが持つ部品**（Issue #523）。`core/theme` の `STAGE_PARTS`（層と HUD の口）に足すと、
 * **共通のトランスフォーム**（位置・スケール・回転・不透明度・アンカー）が自動で付く。
 *
 * 枠の中の要素は `anchors` に出ていない（＝`ANCHOR_PORT` の名前ではない）ので、
 * ここで部品として名乗る。動かし方は `partCss` で CSS の transform を重ねるだけ。
 */
export const HUD_PARTS = Object.freeze([
  { part: 'hud:portrait', label: 'HUD / アイコン', space: 'screen', decor: ['stroke'] },
  { part: 'hud:tag', label: 'HUD / 1P・デッキ名', space: 'screen', decor: ['stroke'] },
  { part: 'hud:bar', label: 'HUD / LP バー', space: 'screen' },
  // **`STAGE_PARTS` に居る部品にも飾りを足せる**（同じ名前なら `toTheme` が混ぜる）
  { part: 'hud:life', decor: ['stroke'] },
  { part: 'hud:hand', decor: ['stroke'] },
].map((x) => Object.freeze(x)));

/**
 * **テーマを詰めるための宣言**（Issue #523。ユーザー要望 2026-09-24）。
 *
 * ここに書いた数値が**そのまま GUI のノブになる**（`part` でどの部品かが決まり、
 * 舞台でその部品を押したときに開く）。**既定値は `HUD_STYLE` が唯一の真実**で、
 * コードには magic number を残さない ——「AI が書いた初稿を人が手で詰める」ための仕組み。
 */
export const HUD_SCHEMA = Object.freeze({
  // 枠の場所（P2 は `hudSymmetry` で反対側へ）
  plateW: { type: 'number', label: '枠の幅', min: 200, max: 900, step: 1, part: 'hud:plate' },
  plateH: { type: 'number', label: '枠の高さ', min: 80, max: 400, step: 1, part: 'hud:plate' },
  plateTop: { type: 'number', label: '上からの距離', min: 0, max: 600, step: 1, part: 'hud:plate' },
  plateSide: { type: 'number', label: '端からの距離', min: 0, max: 600, step: 1, part: 'hud:plate' },
  hudSymmetry: {
    type: 'select',
    label: 'P2 の置き方',
    part: 'hud:plate',
    options: [['左右対称', 'mirror'], ['点対称', 'point'], ['別々', 'free']],
  },
  plateSlope: {
    type: 'select',
    label: '内側の辺',
    part: 'hud:plate',
    options: [['盤の辺に沿う', 'board'], ['まっすぐ', 0], ['ゆるい', 0.15], ['ふつう', 0.3], ['きつい', 0.45]],
  },
  // 枠の中身
  platePad: { type: 'number', label: '余白', min: 0, max: 60, step: 1, part: 'hud:plate' },
  plateIconGap: { type: 'number', label: 'アイコンの間', min: 0, max: 60, step: 1, part: 'hud:plate' },
  plateInnerInset: { type: 'number', label: '内側の辺の余白', min: 0, max: 80, step: 1, part: 'hud:plate' },
  plateGap: { type: 'number', label: '段の間', min: 0, max: 40, step: 1, part: 'hud:plate' },
  plateBarH: { type: 'number', label: 'LP バーの高さ', min: 10, max: 120, step: 1, part: 'hud:life' },
  plateTagFont: { type: 'number', label: '1P / デッキ名の大きさ', min: 10, max: 80, step: 1, part: 'hud:plate' },
  plateNameFont: { type: 'number', label: 'プレイヤー名の大きさ', min: 10, max: 80, step: 1, part: 'hud:plate' },
  plateLifeFontRatio: { type: 'number', label: 'LP の数字の大きさ（枠の高さ比）', min: 0.05, max: 0.6, step: 0.005, part: 'hud:life' },
  plateHandFontRatio: { type: 'number', label: '手札の枚数の大きさ（枠の高さ比）', min: 0.05, max: 0.4, step: 0.005, part: 'hud:hand' },
  plateHandMinH: { type: 'number', label: '手札の段の下限', min: 0, max: 120, step: 1, part: 'hud:hand' },
  plateHandInset: { type: 'number', label: '手札の右の余白', min: 0, max: 60, step: 1, part: 'hud:hand' },
  plateTagYRatio: { type: 'number', label: '1P の段の上（余白比）', min: 0, max: 2, step: 0.05, part: 'hud:plate' },
  plateHandPadRatio: { type: 'number', label: '手札の段の下（余白比）', min: 0, max: 2, step: 0.05, part: 'hud:hand' },
  handCxRatio: { type: 'number', label: '手札の中心の寄せ', min: 0, max: 1, step: 0.01, part: 'hud:hand' },
  // ターンの紋章
  turnBadgeY: { type: 'number', label: '上からの距離', min: 0, max: 600, step: 1, part: 'hud:turnBadge' },
  turnBadgeW: { type: 'number', label: '幅の下限', min: 60, max: 900, step: 1, part: 'hud:turnBadge' },
  turnBadgeH: { type: 'number', label: '高さ', min: 30, max: 300, step: 1, part: 'hud:turnBadge' },
  turnPhaseFontRatio: { type: 'number', label: '文字の大きさ（高さ比）', min: 0.2, max: 1, step: 0.01, part: 'hud:turnBadge' },
  turnPhaseMinFont: { type: 'number', label: '文字の下限', min: 10, max: 120, step: 1, part: 'hud:turnBadge' },
  turnBadgeHeightRatio: { type: 'number', label: '高さの伸び', min: 1, max: 3, step: 0.05, part: 'hud:turnBadge' },
  turnBadgePad: { type: 'number', label: '左右の余白', min: 0, max: 200, step: 1, part: 'hud:turnBadge' },
  /* ---- 色・書体・飾り（CSS カスタムプロパティになる。`arcade.css` が `var()` で読む）---- */
  // 書体と地の色
  hudFont: { type: 'text', label: '書体', part: 'hud:plate', css: '--hud-font' },
  hudColor: { type: 'color', label: '文字の色', part: 'hud:plate', css: '--hud-color' },
  strokeColor: { type: 'color', label: '縁取りの色', part: 'hud:plate', css: '--stroke-color' },
  // 枠の飾り
  frameDark: { type: 'color', label: '枠の縁', part: 'hud:plate', css: '--frame-dark' },
  frameLight: { type: 'color', label: '枠の内線', part: 'hud:plate', css: '--frame-light' },
  plateEdgeW: { type: 'number', label: '縁の太さ', min: 0, max: 30, step: 0.5, part: 'hud:plate', css: '--plate-edge-w' },
  plateInnerW: { type: 'number', label: '内線の太さ', min: 0, max: 20, step: 0.5, part: 'hud:plate', css: '--plate-inner-w' },
  plateShadowA: { type: 'number', label: '影の濃さ', min: 0, max: 1, step: 0.05, part: 'hud:plate', css: '--plate-shadow-a' },
  frameShadowA: { type: 'number', label: '落とす影の濃さ', min: 0, max: 1, step: 0.05, part: 'hud:plate', css: '--frame-shadow-a' },
  turnGlow: { type: 'number', label: 'ターン中の光', min: 0, max: 60, step: 1, part: 'hud:plate', css: '--turn-glow' },
  slash: { type: 'number', label: '斜めの角度', min: -45, max: 45, step: 1, part: 'hud:plate', css: '--slash' },
  // アイコン
  portraitBg: { type: 'color', label: 'アイコンの地', part: 'hud:plate', css: '--portrait-bg' },
  portraitBorder: { type: 'number', label: 'アイコンの縁', min: 0, max: 20, step: 1, part: 'hud:plate', css: '--portrait-border' },
  portraitFont: { type: 'number', label: 'アイコンの文字', min: 20, max: 160, step: 1, part: 'hud:plate', css: '--portrait-font' },
  // LP バーと数字
  lifeMax: { type: 'color', label: 'LP バー（満タン）', part: 'hud:life', css: '--life-max' },
  lifeHigh: { type: 'color', label: 'LP バー（多い）', part: 'hud:life', css: '--life-high' },
  lifeMedium: { type: 'color', label: 'LP バー（中くらい）', part: 'hud:life', css: '--life-medium' },
  lifeLow: { type: 'color', label: 'LP バー（少ない）', part: 'hud:life', css: '--life-low' },
  lifeGhost: { type: 'text', label: 'LP の残像', part: 'hud:life', css: '--life-ghost' },
  barFrame: { type: 'color', label: 'バーの枠', part: 'hud:life', css: '--bar-frame' },
  barBorder: { type: 'number', label: 'バーの枠の太さ', min: 0, max: 20, step: 1, part: 'hud:life', css: '--bar-border' },
  barPad: { type: 'number', label: 'バーの内側の余白', min: 0, max: 20, step: 1, part: 'hud:life', css: '--bar-pad' },
  barTick: { type: 'text', label: 'バーの目盛', part: 'hud:life', css: '--bar-tick' },
  lifeText: { type: 'color', label: 'LP の数字の色', part: 'hud:life', css: '--life-text' },
  lifeLetter: { type: 'number', label: 'LP の字間', min: -0.1, max: 0.3, step: 0.01, part: 'hud:life', css: '--life-letter' },
  // 手札
  handBorder: { type: 'number', label: '手札の縁', min: 0, max: 20, step: 1, part: 'hud:hand', css: '--hand-border' },
  // ターンの紋章
  turnBody: { type: 'text', label: '紋章の地', part: 'hud:turnBadge', css: '--turn-body' },
  turnBlur: { type: 'number', label: '後ろのぼかし', min: 0, max: 30, step: 1, part: 'hud:turnBadge', css: '--turn-blur' },
  turnRingW: { type: 'number', label: '輪の太さ', min: 0, max: 20, step: 0.5, part: 'hud:turnBadge', css: '--turn-ring-w' },
  battleColor: { type: 'color', label: 'バトルフェイズの色', part: 'hud:turnBadge', css: '--battle-color' },
  // 中身の出し入れ
  showHand: { type: 'boolean', label: '手札を出す', part: 'hud:hand' },
  portraitSource: {
    type: 'select',
    label: 'アイコンの絵',
    part: 'hud:plate',
    options: [['デッキのイラスト', 'deck'], ['プレイヤーの画像', 'player']],
  },
});

/** 既定値（**ここが唯一の真実**。`schema` のキーをすべて持つ） */
export const HUD_STYLE = Object.freeze({
  plateW: 470, plateH: 190, plateTop: 0, plateSide: 0,
  hudSymmetry: 'mirror', plateSlope: 'board',
  platePad: 14, plateIconGap: 18, plateInnerInset: 26, plateGap: 6, plateBarH: 46,
  plateTagFont: 30, plateNameFont: 30, plateLifeFontRatio: 0.24, plateHandFontRatio: 0.145,
  plateHandMinH: 40, plateHandInset: 10, plateTagYRatio: 0.7, plateHandPadRatio: 0.8,
  handCxRatio: 0.35,
  turnBadgeY: 0, turnBadgeW: 180, turnBadgeH: 72,
  turnPhaseFontRatio: 0.55, turnPhaseMinFont: 28, turnBadgeHeightRatio: 1.5, turnBadgePad: 44,
  showHand: true, portraitSource: 'deck',
  // 色・書体・飾り（**ここが唯一の真実**。`arcade.css` には既定を書かない）
  hudFont: '"Yu Gothic UI", "Meiryo", "Hiragino Kaku Gothic ProN", system-ui, sans-serif',
  hudColor: '#ffffff', strokeColor: '#101010',
  frameDark: '#1f1f1f', frameLight: '#ffffff',
  plateEdgeW: 10, plateInnerW: 2.5, plateShadowA: 0.35, frameShadowA: 0.45, turnGlow: 18, slash: -12,
  portraitBg: '#2a2f40', portraitBorder: 5, portraitFont: 64,
  lifeMax: '#41ebd8', lifeHigh: '#41eb65', lifeMedium: '#e5eb41', lifeLow: '#eb4141',
  lifeGhost: 'rgba(235, 65, 65, 0.55)',
  barFrame: '#000000', barBorder: 5, barPad: 3, barTick: 'rgba(0, 0, 0, 0.55)',
  lifeText: '#ffffff', lifeLetter: 0.02,
  handBorder: 3,
  turnBody: 'rgba(11, 14, 24, 0.18)', turnBlur: 6, turnRingW: 6, battleColor: '#ffd23a',
  /*
   * **飾り**（Issue #523）。組（`stroke`）は `HUD_PARTS` が名乗り、宣言は `core/decor` が生やす。
   * ここに書くのは**この テーマでの既定値**だけ —— 以前は `nameStroke` / `tagStroke` /
   * `deckStroke` / `lifeStroke` / `handStroke` と**同じものが 5 つの名前**で散っていた。
   */
  'dc/hud:portrait/strokeW': 8,
  'dc/hud:tag/strokeW': 7,
  'dc/hud:life/strokeW': 11,
  'dc/hud:hand/strokeW': 7,
});

/** 既定を当てる（呼ぶ側は `style` を省略できる ＝ 既存の呼び出しがそのまま通る） */
export const hudStyle = withDefaults(() => [HUD_STYLE]);

/* ------------------------------------------------------------------ *
 *  寸法（純関数）
 * ------------------------------------------------------------------ */

/** 枠の中の部品の寸法の基準にした枠の高さ */
export const PLATE_BASE_H = 230;

/** 枠の既定の大きさ（盤は縦長で上の左右が開くので、そこに 1 人 1 枚） */
export const PLATE_SIZE = Object.freeze({ w: HUD_STYLE.plateW, h: HUD_STYLE.plateH });

export const LIFE_BAR_MAX = 8000;

/** LP バーの色の段 */
export function lifeClass(life) {
  if (life >= 8000) return 'is-max';
  if (life >= 5000) return 'is-high';
  if (life >= 2000) return 'is-medium';
  return 'is-low';
}

/** 1 行の中の一番小さい子（「TURN」）の em 比 */
export const TP_LABEL_EM = 0.72;

/** 箱の高さ → ターン・フェイズの 1 行の文字の大きさ */
export const turnPhaseFont = (boxH, style = null) => {
  const v = hudStyle(style);
  return fontPx(Math.max(v.turnPhaseMinFont, Math.round(boxH * v.turnPhaseFontRatio)), TP_LABEL_EM);
};

/** 紋章の高さ（箱の高さを下限に、1 行が収まるまで伸ばす） */
export const turnBadgeHeight = (box, style = null) => (
  Math.max(box.h, Math.round(turnPhaseFont(box.h, style) * hudStyle(style).turnBadgeHeightRatio))
);

/**
 * 1 行の幅の見積もり。**DOM を測らない**（純関数。あとで繋ぐ演出が横に物を置くのに使う）。
 * 平均字幅 ≒ 0.6em、TURN の字間ぶん少し広め
 */
export function turnPhaseWidth(values, font, padding = HUD_STYLE.turnBadgePad) {
  const turn = values?.turn > 0 ? `TURN ${values.turn}`.length * 0.66 : 0;
  const phase = values?.phaseLabel ? values.phaseLabel.length * 0.58 : 0;
  const sep = values?.turn > 0 && values?.phaseLabel ? 0.9 : 0;
  return Math.round((turn + phase + sep) * font + padding * 2);
}

/** 紋章の実際の幅（箱の w は下限） */
export const turnBadgeWidth = (values, box, style = null) => (
  Math.max(box.w, turnPhaseWidth(values, turnPhaseFont(box.h, style), hudStyle(style).turnBadgePad))
);

/**
 * 枠の中の部品の位置（P1 基準。P2 は左右反転して置く）。
 *
 * 文字は**下限を下回らない**ので、段の高さは箱の高さの比ではなく実際の文字の大きさから決め、
 * 入り切らなければ**枠の高さを伸ばす**（`g.h`）。アイコンの一辺だけは配置の箱の高さから決める
 * （枠が伸びてもアイコンは大きくしない＝文字の列を狭めない）。
 */
export function plateGeometry(box, slope = 0, style = null) {
  const v = hudStyle(style);
  const k = box.h / PLATE_BASE_H;
  const pad = v.platePad * k;
  const S = Math.round(box.h - pad * 2 - v.plateIconGap * k);        // アイコンの一辺（下に名前）
  const contentX = pad + S + v.plateIconGap * k;
  const innerEdge = (y) => box.w - slope * y - v.plateInnerInset * k;   // その高さでの斜めの辺の手前
  const font = {
    tag: fontPx(v.plateTagFont * k),                 // 1P / 2P とデッキ名
    life: fontPx(box.h * v.plateLifeFontRatio),      // LP の数字
    hand: fontPx(box.h * v.plateHandFontRatio),      // 手札の枚数
    name: fontPx(v.plateNameFont * k),               // アイコンの下のプレイヤー名
  };
  const gap = Math.round(v.plateGap * k);
  const barH = Math.round(v.plateBarH * k);
  const tagH = Math.round(font.tag * 1.05);
  const lifeH = Math.round(font.life * 1.02);
  const handH = Math.max(Math.round(font.hand * 1.15), Math.round(v.plateHandMinH * k));
  const nameH = Math.round(font.name * 1.12);
  const tagY = Math.round(pad * v.plateTagYRatio);
  const barY = tagY + tagH + gap;
  const lifeY = barY + barH + gap;
  // 右の列（タグ → バー → LP → 手札）とアイコン＋名前の、高いほうが枠の高さ
  const h = Math.round(Math.max(box.h, lifeY + lifeH + gap + handH + pad * v.plateHandPadRatio, pad + S + nameH));
  const handY = h - Math.round(pad * v.plateHandPadRatio) - handH;
  return {
    pad, S, k, h, font,
    tag: { x: contentX, y: tagY, h: tagH, w: innerEdge(tagY + tagH) - contentX },
    bar: { x: contentX, y: barY, w: innerEdge(barY + barH) - contentX, h: barH },
    life: { right: box.w - innerEdge(lifeY + lifeH), y: lifeY, size: font.life },
    hand: { x: contentX, y: handY, w: innerEdge(h) - contentX - v.plateHandInset * k, h: handH },
    name: { h: nameH, size: font.name },
    outline: [[0, 0], [box.w, 0], [box.w - slope * h, h], [0, h]],
  };
}

/** 手札の裏面を並べる位置（箱の中の left と、カードの幅・高さ） */
export function handLayout(count, box, side) {
  const cardH = box.h * 0.67;
  const cardW = cardH * 0.59;
  const inner = box.w - 20;   // 左右に 10 ずつ余白
  const step = count > 1 ? Math.min(cardW * 0.88, (inner - cardW) / (count - 1)) : 0;
  const span = count > 0 ? cardW + step * (count - 1) : 0;
  const start = side === 'left' ? 10 : box.w - 10 - span;
  return { cardW, cardH, top: box.h - cardH, lefts: Array.from({ length: count }, (_, i) => start + step * i) };
}

/** アイコンの画像（deck ならデッキのイラスト、無ければプレイヤーの画像） */
export const portraitImage = (player, source) => (
  (source === 'player' ? player?.image : (player?.deckImage || player?.image)) ?? null
);

/* ------------------------------------------------------------------ *
 *  場所（`core/hud` の ANCHOR_PORT ＋ このテーマだけが読む plateSlope）
 * ------------------------------------------------------------------ */

/**
 * 盤の左の辺の傾き（下へ 1px 行くごとに内側へずらす量）。
 * 盤が写っていない・辺が横向き（90° 回転のショット）なら null。
 */
export function slopeFromView(view) {
  const q = view?.outline;
  if (!q) return null;
  const [tl, , , bl] = q;
  const dy = bl[1] - tl[1];
  if (!(Math.abs(dy) > 1)) return null;
  return Math.max(0, Math.min(1, -(bl[0] - tl[0]) / dy));
}

/**
 * 枠の箱（舞台の大きさから）。**P2 は `hudSymmetry` で反対側へ**（Issue #523）——
 * 左右対称 / 点対称 / 別々 が**選べる値**になっているので、「対称スライダー」と「自由配置」を
 * 分ける必要がない。`options.plates` を渡せば丸ごと差し替えられる（今までどおり）。
 */
export function plateBoxes(canvas, options, style = null) {
  if (options?.plates) return options.plates;
  const v = hudStyle(style);
  const p1 = { x: v.plateSide, y: v.plateTop, w: v.plateW, h: v.plateH };
  return [p1, mirrorBox(p1, canvas, v.hudSymmetry)];
}

export function anchors({ canvas, view, options, style }) {
  const v = hudStyle(style);
  const plates = plateBoxes(canvas, options, style);
  const badge = options?.turnBadge ?? { x: canvas.w / 2, y: v.turnBadgeY, w: v.turnBadgeW, h: v.turnBadgeH };
  const asked = v.plateSlope;
  const slope = asked === 'board' ? (slopeFromView(view) ?? 0.3) : (Number.isFinite(asked) ? asked : 0.3);
  const plate = (p) => plates[p] ?? null;
  return {
    plate,
    /**
     * **LP の数字が実際に出ている箱**（Issue #523）。以前は「枠のすぐ下」を答えていたが、
     * 絵はそこに無い ＝ 契約が嘘だった（舞台で `hud:life` を押しても別の場所が光る）。
     * 幅は文字の見積もり（**DOM を測らない**。4 桁の等幅 ≒ 3em）。
     */
    life(p) {
      const b = plate(p);
      if (!b) return null;   // core が舞台基準の fallback を入れる
      const g = plateGeometry(b, slope, style);
      const w = Math.round(g.life.size * 3);
      const h = Math.round(g.life.size * 1.1);
      return { x: p === 0 ? b.x + b.w - g.life.right - w : b.x + g.life.right, y: b.y + g.life.y, w, h };
    },
    /** 枠の中の手札の段（x, y は中心） */
    hand(p) {
      const b = plate(p);
      if (!b) return null;
      const g = plateGeometry(b, slope, style);
      const cx = g.hand.x + g.hand.w * v.handCxRatio;
      return { x: b.x + (p === 0 ? cx : b.w - cx), y: b.y + g.hand.y + g.hand.h / 2, w: g.hand.w, h: g.hand.h };
    },
    /** **そのフレームの値**で幅が決まる（ターン数とフェイズ名の文字数） */
    turnBadge: (values) => ({
      x: badge.x,
      y: badge.y,
      w: turnBadgeWidth(values ?? { turn: 0 }, badge, style),
      h: turnBadgeHeight(badge, style),
      baseH: badge.h,
    }),
    /** このテーマだけが読む（枠の内側の辺の傾き。`core/hud` はそのまま通す） */
    plateSlope: slope,
  };
}

/* ------------------------------------------------------------------ *
 *  絵（DOM）
 * ------------------------------------------------------------------ */

const pct = (life) => `${Math.min(1, Math.max(0, life / LIFE_BAR_MAX)) * 100}%`;
const mirrorX = (w, x, width = 0) => w - x - width;

/** ターン数とフェイズ名の 1 行（**大きく 1 行**） */
export function TurnPhaseLine({ values, font, color, className = '' }) {
  if (!(values.turn > 0) && !values.phaseLabel) return null;
  const battle = values.phaseLabel === 'Battle Phase';
  return (
    <div className={`de-tp ${className}`} style={{ fontSize: font, '--player-color': color }} data-testid="hud-turnphase">
      {values.turn > 0 ? (
        <span className="de-tp__turn">
          <span className="de-tp__label">TURN</span>
          <span className="de-tp__no" data-testid="hud-turn">{values.turn}</span>
        </span>
      ) : null}
      {values.turn > 0 && values.phaseLabel ? <span className="de-tp__sep" /> : null}
      {values.phaseLabel ? <span className={`de-tp__phase${battle ? ' de-tp__phase--battle' : ''}`} data-testid="hud-phase">{values.phaseLabel}</span> : null}
    </div>
  );
}

function Plate({ player, index, values, box, slope, showHand, portraitSource, style }) {
  const v = hudStyle(style);
  const right = index === 1;
  const g = plateGeometry(box, slope, style);
  const place = (x, width) => (right ? mirrorX(box.w, x, width) : x);
  const outline = g.outline.map(([x, y]) => [right ? box.w - x : x, y]);
  const points = outline.map((p) => p.join(',')).join(' ');
  const active = values.turn > 0 && values.turnPlayer === index;
  const life = values.life[index];
  const ghost = values.lifeGhost[index];
  const handCount = showHand ? values.hands[index] : 0;
  // 裏面は枚数の文字の手前まで（最大 10 枚ぶん並べ、それ以上は枚数の数字で）
  const countW = Math.max(g.hand.h * 1.3, g.font.hand * 1.5);
  const hand = handLayout(Math.min(handCount, 10), { x: 0, y: 0, w: g.hand.w - countW, h: g.hand.h * 1.45 }, right ? 'right' : 'left');
  const image = portraitImage(player, portraitSource);
  const id = `de-hud-${index}`;
  /*
   * **枠の中の要素のトランスフォーム**（Issue #523）。枠そのもの（`hud:plate`）と
   * ターンの紋章（`hud:turnBadge`）は `anchors` の箱に当たっているので**ここでは当てない**
   * （二重になる）。LP と手札は `anchors` と同じ場所を描いているので、同じ値を CSS で重ねる。
   */
  const cssOf = (part, base = null) => partCss(style, part, base);
  /** 縁取り（`core/decor`）。**`-webkit-text-stroke` は継承する**ので、親に当てれば中の文字に効く */
  const inkOf = (part) => strokeCss(style, part);
  return (
    <div
      className={`de-plate de-plate--${right ? 'right' : 'left'}${active ? ' is-turn' : ''}`}
      style={{ ...partStyle({ ...box, h: g.h }), '--player-color': player.color }}
      data-testid={`hud-plate-${index}`}
    >
      <svg className="de-plate__bg" width={box.w} height={g.h} viewBox={`0 0 ${box.w} ${g.h}`} aria-hidden="true">
        <defs>
          <linearGradient id={`${id}-bg`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#15192a" stopOpacity="0.95" />
            <stop offset="1" stopColor="#05070e" stopOpacity="0.88" />
          </linearGradient>
          <pattern id={`${id}-stripe`} width="14" height="14" patternUnits="userSpaceOnUse" patternTransform={`rotate(${right ? -30 : 30})`}>
            <rect width="5" height="14" fill={player.color} opacity="0.12" />
          </pattern>
          <clipPath id={`${id}-clip`}><polygon points={points} /></clipPath>
        </defs>
        <polygon points={points} fill={`url(#${id}-bg)`} />
        <g clipPath={`url(#${id}-clip)`}>
          <rect width={box.w} height={g.h} fill={`url(#${id}-stripe)`} />
          {/* 上の縁のアクセントと、下の縁のテック風の線 */}
          <rect x="0" y="0" width={box.w} height="7" fill={player.color} />
          <rect x={right ? box.w * 0.08 : box.w * 0.42} y={g.h - 12} width={box.w * 0.5} height="3" fill={player.color} opacity="0.7" />
        </g>
        <polygon className="de-plate__edge" points={points} />
        <polygon className="de-plate__inner" points={points} />
      </svg>

      <div className="de-portrait" style={{ left: place(g.pad, g.S), top: g.pad, width: g.S, height: g.S, ...cssOf('hud:portrait') }} data-testid={`hud-portrait-${index}`}>
        <div className="de-portrait__img" style={{ backgroundImage: image ? `url("${image}")` : undefined }}>
          {image ? null : (player.name || '?').slice(0, 1)}
        </div>
        <div className={`de-portrait__name ${INK_CLASS}`} style={{ fontSize: g.name.size, height: g.name.h, lineHeight: `${g.name.h}px`, ...inkOf('hud:portrait') }}>
          {player.name || player.kanji}
        </div>
      </div>

      <div className={`de-tag de-tag--${right ? 'right' : 'left'} ${INK_CLASS}`} style={{ left: right ? undefined : g.tag.x, right: right ? g.tag.x : undefined, top: g.tag.y, height: g.tag.h, ...inkOf('hud:tag'), ...cssOf('hud:tag') }}>
        <span className="de-tag__chip" style={{ fontSize: g.font.tag }}>{index + 1}P</span>
        {/* デッキ名は斜めの辺の手前まで（文字は下限なので、入らない名前は「…」） */}
        <span className="de-tag__deck" style={{ fontSize: g.font.tag, maxWidth: Math.max(120, g.tag.w - g.font.tag * 1.5 - 12) }}>{player.deckName}</span>
      </div>

      <div className={`de-bar de-bar--${right ? 'right' : 'left'} ${lifeClass(life)}`} style={{ left: place(g.bar.x, g.bar.w), top: g.bar.y, width: g.bar.w, height: g.bar.h, ...cssOf('hud:bar', `skewX(${right ? -v.slash : v.slash}deg)`) }}>
        <div className="de-bar__track">
          <div className="de-bar__ghost" style={{ width: pct(ghost) }} />
          <div className="de-bar__fill" style={{ width: pct(life) }} />
          <div className="de-bar__ticks" />
        </div>
      </div>
      <div
        className={`de-life de-tnum de-life--${right ? 'right' : 'left'} ${INK_CLASS}`}
        style={{ [right ? 'left' : 'right']: g.life.right, top: g.life.y, fontSize: g.life.size, ...inkOf('hud:life'), ...cssOf('hud:life') }}
        data-testid={`hud-lp-${index}`}
      >
        {Math.round(life)}
      </div>

      {showHand ? (
        <div className={`de-hand de-hand--${right ? 'right' : 'left'}`} style={{ left: place(g.hand.x, g.hand.w), top: g.hand.y, width: g.hand.w, height: g.hand.h, ...cssOf('hud:hand') }} data-testid={`hud-hand-${index}`}>
          {hand.lefts.map((left, i) => (
            <div
              key={i}
              className="de-hand__card"
              style={{
                left: right ? left + countW : left,
                top: g.hand.h - hand.cardH,
                width: hand.cardW,
                height: hand.cardH,
                backgroundImage: player.sleeveImage ? `url("${player.sleeveImage}")` : undefined,
              }}
            />
          ))}
          <div className={`de-hand__count de-tnum ${INK_CLASS}`} style={{ fontSize: g.font.hand, ...inkOf('hud:hand') }}>×{handCount}</div>
        </div>
      ) : null}
    </div>
  );
}

export function TurnBadge({ values, box, color, style }) {
  if (!(values.turn > 0) && !values.phaseLabel) return null;
  const { w, h } = box;
  const cut = h * 0.35;
  const points = `${cut},0 ${w - cut},0 ${w},${h * 0.45} ${w - cut},${h} ${cut},${h} 0,${h * 0.45}`;
  const clip = `polygon(${points.split(' ').map((pt) => pt.split(',').map((v) => `${v}px`).join(' ')).join(', ')})`;
  return (
    <div className="de-turn" style={{ ...partStyle({ ...box, x: box.x - w / 2 }), '--player-color': color }} data-testid="hud-turntag">
      {/* 六角形の地はガラス風（半透明のグラデーション＋後ろをぼかす）。輪と縁は SVG の線 */}
      <div className="de-turn__glass" style={{ clipPath: clip }} />
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" className="de-turn__bg">
        <polygon points={points} className="de-turn__body" />
        <polygon points={points} className="de-turn__ring" />
        <polygon points={points} className="de-turn__edge" />
      </svg>
      <TurnPhaseLine values={values} font={turnPhaseFont(box.baseH ?? h)} color={color} className="de-turn__line" />
    </div>
  );
}

/**
 * @param values `core/hud::hudValues` を通した「そのフレームの値」
 * @param anchors `core/hud` が口を埋めた場所
 * @param options テーマの設定（`showHand` / `portraitSource` / `plateSlope` …）
 */
export default function ArcadeHud({ values, anchors, options, style }) {
  const v = hudStyle(style);
  const badge = anchors.turnBadge(values);
  const color = values.players[values.turnPlayer]?.color ?? '#888';
  return (
    <div className="de-hud-arcade">
      {[0, 1].map((i) => {
        const box = anchors.plate(i);
        return box ? (
          <Plate
            key={i}
            index={i}
            player={values.players[i]}
            values={values}
            box={box}
            slope={anchors.plateSlope ?? 0}
            style={style}
            showHand={v.showHand !== false}
            portraitSource={v.portraitSource ?? 'deck'}
          />
        ) : null;
      })}
      {badge ? <TurnBadge values={values} box={badge} color={color} style={style} /> : null}
    </div>
  );
}
